// 🔧 promotion_social_connections.js
// ✅ Purpose: Display social publishing connection status for the LGV Promotion Engine
// ✅ Features:
// - Supports Admin MVP1 platform-level LGV social connections
// - Supports Event MVP2 event-scoped social connections when eventslug is present
// - Reads Integration Engine connection status from Firestore
// - Displays Facebook, Instagram, LinkedIn, and TikTok provider status
// - Routes connection management to Integration Engine pages
// - Keeps OAuth management out of Promotion Engine pages
// - Uses shared Promotion Engine components
// - Fully block-commented for clarity and future devs

import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Link,
  useParams,
} from "react-router-dom";

import {
  useEventContext,
} from "../../../hooks/use_event_context";

import {
  getIntegrationConnectionMap,
} from "../../../services/integration_connections";

import PromotionPageHeader from "../../../promotion/components/promotion_page_header";
import PromotionSectionCard from "../../../promotion/components/promotion_section_card";
import PromotionActionNavigation from "../../../promotion/components/promotion_action_navigation";
import PromotionConnectionManager from "../../../promotion/components/promotion_connection_manager";

const SOCIAL_PROVIDERS = [
  {
    key: "facebook",
    label: "Facebook",
    icon: "📘",
    description:
      "Facebook Page publishing through the Integration Engine.",
  },
  {
    key: "instagram",
    label: "Instagram",
    icon: "📸",
    description:
      "Instagram Business integration through the Integration Engine.",
  },
  {
    key: "linkedin",
    label: "LinkedIn",
    icon: "💼",
    description:
      "LinkedIn organization integration through the Integration Engine.",
  },
  {
    key: "tiktok",
    label: "TikTok",
    icon: "🎵",
    description:
      "TikTok Business integration through the Integration Engine.",
  },
];

const PromotionSocialConnections = () => {
  const { eventslug } = useParams();

  const {
    eventId,
    eventDoc,
  } = useEventContext({
    redirectIfNotFound: false,
  });

  const [connections, setConnections] = useState({});
  const [loading, setLoading] = useState(true);

  const isEventScoped =
    Boolean(eventslug && eventId);

  const connectionModeLabel =
    isEventScoped
      ? "Event Social Connections"
      : "LGV Platform Social Connections";

  const manageConnectionsUrl =
    isEventScoped
      ? `/${eventslug}/integrations`
      : "/promotion_social_connections";

  const promotionUrl = (page) => isEventScoped ? `/${eventslug}/${page}` : `/${page}`;

  const connectedCount = useMemo(() => {
    return SOCIAL_PROVIDERS.filter((provider) => {
      const connection = connections?.[provider.key];

      return (
        connection?.status === "connected" ||
        connection?.connected === true
      );
    }).length;
  }, [connections]);

  useEffect(() => {
    setLoading(true);

    if (!isEventScoped) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    getIntegrationConnectionMap(eventId)
      .then((nextConnections) => {
        if (!cancelled) {
          setConnections(nextConnections);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error(
            "[Promotion Social Connections] Failed to load provider connection",
            {
              message: err?.message,
            }
          );
          setConnections({});
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [eventId, isEventScoped]);

  const getConnection = (providerKey) => {
    return connections?.[providerKey] || null;
  };

  const isConnected = (connection) => {
    return (
      connection?.status === "connected" ||
      connection?.connected === true
    );
  };

  const getConnectionName = (connection) => {
    return (
      connection?.selectedPageName ||
      connection?.pageName ||
      connection?.account?.name ||
      connection?.accountName ||
      connection?.name ||
      "Connected account"
    );
  };

  const getConnectionId = (connection) => {
    return (
      connection?.selectedPageId ||
      connection?.pageId ||
      connection?.account?.id ||
      connection?.accountId ||
      ""
    );
  };

  const getBadge = (connection) => {
    if (loading) {
      return {
        className: "bg-secondary",
        label: "Loading",
      };
    }

    if (isConnected(connection)) {
      return {
        className: "bg-success",
        label: "Connected",
      };
    }

    return {
      className: "bg-light text-dark border",
      label: "Not Connected",
    };
  };

  return (
    <div className="container-fluid py-4">
      <div
        className="container"
        style={{
          maxWidth: "1100px",
        }}
      >
        <PromotionPageHeader
          title="Promotion Social Connections"
          subtitle={`${connectionModeLabel} used for Promotion Engine publishing.`}
        />

        {isEventScoped && <PromotionSectionCard
          title="Connection Summary"
          subtitle="Current publishing connection status."
          icon="bi bi-share"
        >
          <div className="row g-3">
            <div className="col-12 col-md-4">
              <div className="border rounded p-3 h-100">
                <div className="small text-muted">
                  Connection Scope
                </div>

                <div className="fw-bold">
                  {isEventScoped ? "Event" : "Platform"}
                </div>

                {isEventScoped && (
                  <div className="small text-muted mt-1">
                    {eventDoc?.name || eventslug}
                  </div>
                )}
              </div>
            </div>

            <div className="col-12 col-md-4">
              <div className="border rounded p-3 h-100">
                <div className="small text-muted">
                  Connected Providers
                </div>

                <div className="fw-bold">
                  {loading
                    ? "Loading..."
                    : `${connectedCount} of ${SOCIAL_PROVIDERS.length}`}
                </div>
              </div>
            </div>

            <div className="col-12 col-md-4">
              <div className="border rounded p-3 h-100">
                <div className="small text-muted">
                  Publishing Gateway
                </div>

                <div className="fw-bold">
                  Integration Engine
                </div>
              </div>
            </div>
          </div>
        </PromotionSectionCard>}

        <PromotionActionNavigation current="connections" promotionUrl={promotionUrl} />

        {isEventScoped ? (
          <>
        <PromotionSectionCard
          title="Event Connection Management"
          subtitle="Social authorization and token handling are managed by the Integration Engine."
          icon="bi bi-plug"
        >
          <div className="alert alert-info mb-3">
            Promotion Engine creates and approves content. Integration Engine
            owns OAuth, saved connections, provider validation, and publishing
            communication with external platforms.
          </div>

          <Link
            className="btn btn-primary"
            to={manageConnectionsUrl}
          >
            🔌 Manage Connections
          </Link>
        </PromotionSectionCard>

        <PromotionSectionCard
          title="Publishing Providers"
          subtitle="Provider connections available to the Promotion Engine."
          icon="bi bi-share"
        >
          {SOCIAL_PROVIDERS.map((provider) => {
            const connection = getConnection(provider.key);
            const badge = getBadge(connection);
            const connected = isConnected(connection);
            const connectionId = getConnectionId(connection);

            return (
              <div
                key={provider.key}
                className="border rounded p-3 mb-3"
              >
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-3">
                  <div>
                    <h5 className="mb-1">
                      {provider.icon} {provider.label}
                    </h5>

                    <p className="text-muted mb-2">
                      {provider.description}
                    </p>

                    {connected ? (
                      <div className="small">
                        <div>
                          <strong>Account:</strong>{" "}
                          {getConnectionName(connection)}
                        </div>

                        {connectionId && (
                          <div className="text-muted">
                            <strong>ID:</strong>{" "}
                            {connectionId}
                          </div>
                        )}

                        {connection?.updatedAt && (
                          <div className="text-muted">
                            <strong>Updated:</strong>{" "}
                            {connection.updatedAt?.toDate
                              ? connection.updatedAt.toDate().toLocaleString()
                              : String(connection.updatedAt)}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="small text-muted">
                        No saved connection found for this scope.
                      </div>
                    )}
                  </div>

                  <span className={`badge ${badge.className}`}>
                    {badge.label}
                  </span>
                </div>
              </div>
            );
          })}
        </PromotionSectionCard>
          </>
        ) : (
          <PromotionConnectionManager embedded />
        )}
      </div>
    </div>
  );
};

export default PromotionSocialConnections;
